import {
  Button,
  Divider,
  Flex,
  HStack,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import { useAuth, useClerk } from "@clerk/clerk-react";
import {
  RiArrowLeftRightLine,
  RiCheckLine,
  RiLogoutBoxLine,
  RiUserLine,
} from "@remixicon/react";
import { useState } from "react";

import OrganizationSwitcher from "@/common/components/OrganizationSwitcher";
import { UserDisplay } from "@/common/components/UserDisplay";
import { useEnvironmentUnsafe } from "@/common/contexts/EnvironmentContext";
import { MemberRoleBadge } from "@/global-settings/components/MemberRoleBadge";
import { useFlag } from "@reflag/react-sdk";
import { MemberRole, MemberRoles } from "@reflag/shared/permissions";

export function AppProfileMenu() {
  const { openUserProfile } = useClerk();
  const { signOut } = useAuth();
  const { user, org, setOverriddenRole, overriddenRole } =
    useEnvironmentUnsafe() ?? {};
  const textColor = useColorModeValue("gray.900", "white");
  const [isOrgSwitcherOpen, setIsOrgSwitcherOpen] = useState(false);
  const { isEnabled: enforceUserPermissions } = useFlag("user-permissions");

  if (!user) {
    return null;
  }

  const handleRoleSelect = (role: MemberRole | null) => {
    setOverriddenRole?.(role);
  };

  return (
    <>
      <VStack align="start" spacing={0}>
        <Menu placement="top-start" size="sm" matchWidth>
          <MenuButton
            _hover={{ color: textColor }}
            as={Button}
            color="dimmed"
            data-testid="profile-button"
            variant="unstyled"
          >
            <Flex align="center" flexDirection="row" gap={2}>
              <UserDisplay
                _avatar={{ size: "xs" }}
                fontSize="sm"
                fontWeight="medium"
                user={user}
                isTruncated
                onlyFirstName
              />
            </Flex>
          </MenuButton>
          <Portal>
            <MenuList minW="max-content" ml={-2}>
              <MenuItem
                fontWeight="medium"
                icon={<RiUserLine size={14} />}
                iconSpacing={1.5}
                onClick={() => {
                  openUserProfile();
                }}
              >
                Account
              </MenuItem>
              <MenuItem
                fontWeight="medium"
                icon={<RiArrowLeftRightLine size={14} />}
                iconSpacing={1.5}
                onClick={(e) => {
                  e.preventDefault();
                  setIsOrgSwitcherOpen(true);
                }}
              >
                Switch organization
              </MenuItem>
              <MenuItem
                fontWeight="medium"
                icon={<RiLogoutBoxLine size={14} />}
                iconSpacing={1.5}
                onClick={async (e) => {
                  e.preventDefault();
                  await signOut();
                }}
              >
                Log out
              </MenuItem>
              {user.isReflagAdmin && org && enforceUserPermissions && (
                <>
                  <Divider mb={2} mt={2} />
                  {MemberRoles.map((role) => {
                    const isSelected =
                      (overriddenRole ?? org.userRole) === role;

                    return (
                      <MenuItem
                        key={role}
                        fontWeight={isSelected ? "semibold" : "medium"}
                        onClick={() => handleRoleSelect(role)}
                      >
                        <HStack spacing={2}>
                          {isSelected && <Icon as={RiCheckLine} boxSize={4} />}
                          <MemberRoleBadge role={role} />
                        </HStack>
                      </MenuItem>
                    );
                  })}
                </>
              )}
            </MenuList>
          </Portal>
        </Menu>
      </VStack>
      <OrganizationSwitcher
        isOpen={isOrgSwitcherOpen}
        onClose={() => setIsOrgSwitcherOpen(false)}
      />
    </>
  );
}
